import styled from '@emotion/styled';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import type { ReviewResponse } from '../../api/reviewApi';
import ToggleIcon from '../../assets/icons/ToggleIcon';
import { useCreateReview } from '../../hooks/useCreateReview';
import { usePageMeta } from '../../hooks/usePageMeta';
import { useProductReviews } from '../../hooks/useProductReviews';
import { Text1gMedium } from '../../styles/typography';
import { Spinner } from './components/common/Spinner';
import ReviewCard from './components/ReviewCard';
import ReviewFilterBar, { sortOptions } from './components/ReviewFilterBar';
import ReviewHeader from './components/ReviewHeader';
import ReviewPagination from './components/ReviewPagination';
import type { ReviewWriteMobileModalValues } from './components/ReviewWriteMobileModal';
import { ReviewWriteMobileModal } from './components/ReviewWriteMobileModal';
import type { ReviewWritePcModalValues } from './components/ReviewWritePcModal';
import { ReviewWritePcModal } from './components/ReviewWritePcModal';

// Styled Components
const Container = styled.div`
  width: 100%;
  background: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  box-sizing: border-box;

  @media (min-width: 768px) {
    max-width: 1280px;
    margin: 0 auto;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const Title = styled.div`
  ${Text1gMedium}
  color: #2e2e2e;
  height: 24px;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const ContentWrapper = styled.div<{ isCollapsed: boolean }>`
  max-height: ${(props) => (props.isCollapsed ? '0' : '10000px')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const Divider = styled.div`
  border: none;
  height: 1px;
  background-color: #e2e2e2;
  margin: 24px 0;

  @media (min-width: 768px) {
    margin: 32px 0;
  }
`;

const WriteReviewButton = styled.button`
  margin-top: 48px;
  width: 100%;
  height: 48px;
  background: #3d1690;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  font-family: 'Plus Jakarta Sans', sans-serif;
  display: block;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    opacity: 0.9;
  }

  @media (min-width: 768px) {
    max-width: 530px;
  }
`;

const ReviewList = styled.div`
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0px 32px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 1px;
      height: 100%;
      background: #e2e2e2;
      z-index: 1;
    }
  }
`;

// Types
export interface ReviewImage {
  id: string;
  url: string;
  alt: string;
}

export interface Review {
  id: string;
  rating: number;
  title: string;
  content: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  verifiedPurchase: boolean;
  createdAt: string;
  likeCount: number;
  skinType: string[];
  skinConcern: string[];
  images?: ReviewImage[];
}

export interface ReviewSectionProps {
  productId?: string;
}

// ReviewResponse -> Review 변환 함수
function mapReviewResponseToReview(r: ReviewResponse): Review {
  return {
    id: String(r.reviewId),
    rating: r.rating,
    title: r.title,
    content: r.body,
    author: {
      id: '', // API에 없음
      name: r.name,
      avatar: '', // API에 없음
    },
    verifiedPurchase: true, // API에 없음(임시)
    createdAt: r.modifiedAt,
    likeCount: 0, // API에 없음(임시)
    skinType: r.skinType,
    skinConcern: r.skinConcern,
    images: r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
    })),
  };
}

export const HomePageReviewSection = ({ productId }: ReviewSectionProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isPcWriteModalOpen, setIsPcWriteModalOpen] = useState(false);
  const [isMobileWriteOpen, setIsMobileWriteOpen] = useState(false);
  const [sort, setSort] = useState<string>(sortOptions.at(0)?.value || '');
  const [skinTypes, setSkinTypes] = useState<string[]>([]);
  const [skinConcerns, setSkinConcerns] = useState<string[]>([]);
  const isPC = useMediaQuery({ minWidth: 768 });
  const [isCollapsed, setIsCollapsed] = useState(false);
  // 리뷰 생성 mutation
  const { mutate: createReview, isPending: isCreatingReview } = useCreateReview();

  // postId fetch
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const { data: pageMeta, isLoading: isPageMetaLoading } = usePageMeta(path, { enabled: !isCollapsed });
  const postId = productId ? Number(productId) : pageMeta?.postId ? Number(pageMeta.postId) : undefined;

  // 리뷰 목록 조회 (React Query)
  const { data: reviewsRaw = [], isLoading: isReviewsLoading } = useProductReviews(postId!);
  const reviews = reviewsRaw.map(mapReviewResponseToReview);

  // 모든 리뷰의 pictureUrls를 합쳐서 customerImages 배열 생성
  const customerImages = reviewsRaw.flatMap((r) =>
    r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
    })),
  );

  const filteredReviews = useMemo(() => {
    let result = reviews || [];

    switch (sort) {
      case 'newest':
        result = result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        result = result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case 'highest':
        result = result.sort((a, b) => b.rating - a.rating);
        break;
      case 'lowest':
        result = result.sort((a, b) => a.rating - b.rating);
        break;
    }

    if (skinTypes.length > 0) {
      result = result.filter((review) => skinTypes.some((type) => review.skinType.includes(type)));
    }

    if (skinConcerns.length > 0) {
      result = result.filter((review) => skinConcerns.some((concern) => review.skinConcern.includes(concern)));
    }

    return result;
  }, [reviews, sort, skinTypes, skinConcerns]);

  const ITEMS_PER_PAGE = 4;
  const totalReviewsLength = reviews.length;
  const filteredTotalPages = Math.ceil((filteredReviews?.length || 0) / ITEMS_PER_PAGE);
  // 평균 평점 계산 (소수점 한자리)
  const averageRating =
    totalReviewsLength === 0
      ? 0
      : Number((reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviewsLength).toFixed(1));

  // ratingDistribution 계산 (1~5점별 개수)
  const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  reviews.forEach((r) => {
    const rating = Math.round(r.rating);
    if (rating >= 1 && rating <= 5) {
      ratingDistribution[rating] += 1;
    }
  });

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentReviews = filteredReviews?.slice(startIndex, endIndex) || [];
  const hasReviews = reviews.length > 0;
  const productImage = pageMeta?.image || '';

  // 모바일 리뷰 작성 모달이 열렸을 때 body 스크롤 막기
  useEffect(() => {
    if (!isPC && isMobileWriteOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileWriteOpen, isPC]);

  const handleViewAllImages = () => {
    setSelectedImageIndex(0);
    setIsBottomSheetOpen(true);
  };

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false);
    setSelectedImageIndex(null);
  };

  const handleClickWriteReview = () => {
    if (isPC) {
      setIsPcWriteModalOpen(true);
    } else {
      setIsMobileWriteOpen(true);
    }
  };

  const handleLikeClick = (reviewId: string) => {
    console.info('reviewId', reviewId);
  };

  const handleCloseWriteModal = useCallback(() => setIsPcWriteModalOpen(false), []);

  const handleCloseMobileWriteModal = useCallback(() => setIsMobileWriteOpen(false), []);

  const handleMobileReviewSubmit = useCallback(
    (values: ReviewWriteMobileModalValues) => {
      return new Promise<void>((resolve, reject) => {
        createReview(
          {
            name: values.nickname,
            rating: values.rating,
            body: values.content,
            productId: postId!,
            title: values.title,
            files: values.images,
            skinType: [values.skinType],
            skinConcern: [values.skinConcern],
          },
          {
            onSuccess: () => {
              resolve();
            },
            onError: () => {
              reject();
            },
          },
        );
      });
    },
    [createReview, postId],
  );

  const handlePcReviewSubmit = useCallback(
    (values: ReviewWritePcModalValues) => {
      return new Promise<void>((resolve, reject) => {
        const files: File[] = [];
        createReview(
          {
            name: values.nickname,
            email: '', // 이메일 입력값 필요
            rating: values.rating,
            body: values.content,
            productId: postId!,
            title: values.title,
            files,
            skinType: [values.skinType],
            skinConcern: [values.skinConcern],
          },
          {
            onSuccess: () => {
              resolve();
            },
            onError: () => {
              reject();
            },
          },
        );
      });
    },
    [createReview, postId],
  );

  const handleSortChange = (sortBy: string) => {
    setCurrentPage(1);
    setSort(sortBy);
  };

  const handleSkinTypeChange = (skinTypes: string[]) => {
    setCurrentPage(1);
    setSkinTypes(skinTypes);
  };

  const handleSkinConcernChange = (skinConcerns: string[]) => {
    setCurrentPage(1);
    setSkinConcerns(skinConcerns);
  };

  return (
    <Container id="judgeme_product_reviews">
      <TitleWrapper onClick={() => setIsCollapsed(!isCollapsed)}>
        <Title id="judgeme_product_reviews">Reviews</Title>
        <ToggleIcon isCollapsed={isCollapsed} />
      </TitleWrapper>

      <ContentWrapper isCollapsed={isCollapsed}>
        <ReviewHeader
          averageRating={averageRating}
          totalReviews={totalReviewsLength}
          ratingDistribution={ratingDistribution}
          customerImages={customerImages}
          reviews={reviews}
          onViewAllImages={handleViewAllImages}
        />

        {hasReviews && !isPageMetaLoading && !isReviewsLoading && (
          <>
            {/* ImageBottomSheet는 리뷰 이미지 목록이 필요할 때만 사용하도록 수정 필요. */}

            <Divider />

            <ReviewFilterBar
              onSortChange={handleSortChange}
              onSkinTypeChange={handleSkinTypeChange}
              onSkinConcernChange={handleSkinConcernChange}
            />

            <ReviewList>
              {currentReviews.map((review) => (
                <ReviewCard key={review.id} review={review} onLikeClick={handleLikeClick} />
              ))}
            </ReviewList>

            <ReviewPagination currentPage={currentPage} totalPages={filteredTotalPages} onPageChange={setCurrentPage} />
          </>
        )}
        {(isPageMetaLoading || isReviewsLoading) && <Spinner />}

        <WriteReviewButton onClick={handleClickWriteReview} disabled={isCreatingReview}>
          Write a review
        </WriteReviewButton>
      </ContentWrapper>

      {isPC && (
        <ReviewWritePcModal
          isOpen={isPcWriteModalOpen}
          productImage={productImage}
          onClose={handleCloseWriteModal}
          onSubmit={handlePcReviewSubmit}
        />
      )}
      {!isPC && (
        <ReviewWriteMobileModal
          isOpen={isMobileWriteOpen}
          productImage={productImage}
          onClose={handleCloseMobileWriteModal}
          onSubmit={handleMobileReviewSubmit}
        />
      )}
    </Container>
  );
};

export default HomePageReviewSection;
